// Phase T1.3 — Leg correlation doctrine (v3.0 Tier 1, promoted ahead
// of NEL per the Master Roadmap Option 2).
//
// Pure TypeScript, zero I/O. Accumulator legs are NOT independent: a
// high-scoring game produces BTTS Yes AND Over 2.5 together, so
// stacking both gives the user LESS combined probability than the
// product of individual prices implies. This module surfaces that —
// it never removes a leg (N12: advisory, never exclusionary).
//
// The coefficients are a defensible v3.0 starting point; the SE-8
// CLV eval loop will calibrate them against real STRIKZA data.

import type { ScoredLeg, SignalDriver } from "./legScorer.ts";

/** Coarse market "kind" used for correlation lookup. */
export type MarketKind =
  | "btts_yes"
  | "btts_no"
  | "over"
  | "under"
  | "home"
  | "away"
  | "draw"
  | "correct_score"
  | "other";

/** Classify a leg's (market, selection) into a coarse kind. Pure. */
export function legMarketKind(leg: {
  market?: string;
  selection?: string;
}): MarketKind {
  const m = String(leg?.market ?? "").toLowerCase();
  const s = String(leg?.selection ?? "").toLowerCase();
  if (/correct score|scoreline/.test(m)) return "correct_score";
  if (/btts|both teams/.test(m)) {
    if (/^\s*(y|yes)/.test(s)) return "btts_yes";
    if (/^\s*(n|no)/.test(s)) return "btts_no";
    return "other";
  }
  if (/over|under|total|o\/u|goals/.test(m) || /\bover\b|\bunder\b/.test(s)) {
    if (/over/.test(s)) return "over";
    if (/under/.test(s)) return "under";
    return "other";
  }
  if (/match winner|1x2|result|moneyline|winner|full time/.test(m)) {
    if (/draw|^\s*x\s*$|tie/.test(s)) return "draw";
    if (/\bhome\b|^\s*1\s*$/.test(s)) return "home";
    if (/\baway\b|^\s*2\s*$/.test(s)) return "away";
    return "other";
  }
  return "other";
}

// Unordered pair doctrine table (v3.0 starting point, spec §T1.3).
const PAIR_RHO: Record<string, number> = {
  "btts_yes|over": 0.65,
  "btts_no|home": 0.45,
  "correct_score|over": 0.55,
  // Symmetric, intuitive extensions of the same doctrine:
  "btts_yes|under": -0.5, // BTTS Yes pulls against Under (anti-corr)
  "btts_no|under": 0.45,
  "btts_yes|home": 0.2,
};

function pairKey(a: MarketKind, b: MarketKind): string {
  return [a, b].sort().join("|");
}

/** Correlation coefficient for two legs. Legs in different fixtures
 *  are treated as independent (0.0). Pure. */
export function pairCorrelation(
  legA: { fixture_id?: string | null; market?: string; selection?: string },
  legB: { fixture_id?: string | null; market?: string; selection?: string },
): number {
  const fa = legA?.fixture_id ?? null;
  const fb = legB?.fixture_id ?? null;
  if (!fa || !fb || fa !== fb) return 0;
  const rho = PAIR_RHO[pairKey(legMarketKind(legA), legMarketKind(legB))];
  return typeof rho === "number" ? rho : 0;
}

/** Correlation-adjusted joint probability of two independent-priced
 *  legs. Gaussian-copula-ish first-order approximation:
 *    P(A∩B) ≈ pA·pB + ρ·√(pA(1−pA)·pB(1−pB))
 *  Clamped to a valid probability. Pure. */
export function adjustedJointProbability(
  pA: number,
  pB: number,
  rho: number,
): number {
  const a = Math.min(1, Math.max(0, pA));
  const b = Math.min(1, Math.max(0, pB));
  const cov = rho * Math.sqrt(a * (1 - a) * b * (1 - b));
  return Math.min(1, Math.max(0, a * b + cov));
}

/** T1.3 threshold — only |ρ| above this surfaces a warning. */
export const CORRELATION_THRESHOLD = 0.4;

/** Post-filter advisory pass. Returns a NEW kept list where every leg
 *  in a correlated same-fixture pair carries a `correlation_warning`
 *  driver (contribution_pp 0 — it never moves the score, N12). No leg
 *  is removed. Pure. */
export function annotateCorrelations(kept: ScoredLeg[]): ScoredLeg[] {
  if (!Array.isArray(kept) || kept.length < 2) return kept;
  const extra: Map<number, SignalDriver[]> = new Map();

  for (let i = 0; i < kept.length; i++) {
    for (let j = i + 1; j < kept.length; j++) {
      const legI = kept[i]!;
      const legJ = kept[j]!;
      const rho = pairCorrelation(legI, legJ);
      if (Math.abs(rho) < CORRELATION_THRESHOLD) continue;
      const pA = typeof legI.pWin === "number" ? legI.pWin : 0;
      const pB = typeof legJ.pWin === "number" ? legJ.pWin : 0;
      const naive = pA * pB;
      const adj = adjustedJointProbability(pA, pB, rho);
      const fxt = [legI.home_team, legI.away_team]
        .filter(Boolean)
        .join(" v ");
      const evidence =
        `Correlated with another kept leg in ${fxt || "the same fixture"} ` +
        `(ρ≈${rho.toFixed(2)}). Independence assumption overstates the ` +
        `pair: naive ${(naive * 100).toFixed(0)}% vs ` +
        `correlation-adjusted ${(adj * 100).toFixed(0)}%. ` +
        `You decide — nothing was removed.`;
      const d: SignalDriver = {
        source: "correlation_warning",
        contribution_pp: 0,
        evidence,
        weight: 0,
      };
      for (const idx of [i, j]) {
        const arr = extra.get(idx) ?? [];
        // One warning per leg is enough even if it correlates with
        // several others — keep the strongest |ρ|.
        if (arr.length === 0) arr.push(d);
        extra.set(idx, arr);
      }
    }
  }

  if (extra.size === 0) return kept;
  return kept.map((leg, idx) => {
    const add = extra.get(idx);
    if (!add) return leg;
    return { ...leg, drivers: [...leg.drivers, ...add] };
  });
}
